import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { device, breakpoints } from '../../../../app/js/utils/responsive_utilities';
import { fontQuicksand } from '../../common/mixins/mixin';
import Styled from 'styled-components';
import Config from '../../../../app/js_bytesize/config/purchase/config';
import PaymentCta from './PurchaseCta';
import Image from '../../../../app/js/components/loggedout/common/Image';

import Visa from '~images/mela-purchase/visa.svg';
import Amex from '~images/mela-purchase/amex.svg';
import MasterCard from '~images/mela-purchase/mastercard.svg';
import Discover from '~images/mela-purchase/discover.svg';
import { FormElement, InputBox } from "../../common/styled-components/styled-components"
import { getTrialEndDate } from "../../../../app/js/utils/web-helper"
import { ValidateOfferCode } from "../../../../app/js/services/parent-api"
import * as S from "./promo-page/style";
import { mintGreen, orange } from '../../../../app/js/components/loggedout/shared-styles/Colors';
import tick from "~images/sem/tick.png";

const CARD_ELEMENT_STYLES = {
  style: {
    base: {
      color: '#fff',
      fontFamily: 'QuickSand, Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      fontWeight: 700,
      letterSpacing: '2px',
      '::placeholder': {
        color: '#313575'
      },
      ':-webkit-autofill': {
        backgroundColor: 'transparent'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

const CheckoutForm = ({ settings, paymentHandler, error, setError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [promo, setPromo] = useState("");
  const [promoValid, setPromoValid] = useState(false);
  const [postalCode, setPostalCode] = useState();
  const [eventLogged, setEventLogged] = useState(false);
  const [email, setEmail] = useState("");
  const blurActiveElementRef = useRef();
  const [cardNumberEnterInitiated,setCardNumberEnterInitiated] = useState(false); 
  const [promoCodeError, setPromoCodeError] = useState("");
  const [mastercardError, setMastercardError] = useState(false);
  const promoValidMessage = settings.campaignCode === "miles"?`Congratulations, you have unlocked 30-day free subscription and 30% off on yearly plans.`:`Congratulations, you have unlocked free trial for 3 months and 50% off on yearly plans.`;

  const errorElement = useRef(null);
  
  //Hides the keyboard whenever the focus is on input or textarea field and the user taps outside of that element
  blurActiveElementRef.current = function (e) {
    if (!isTextInput(e.target) && isTextInput(document.activeElement)) {
      document.activeElement.blur();
    }
  }
  let { showZipOnPurchase, trailDays } = settings

  useEffect(() => {
    const creditFormElement = document.getElementById("credit-card-form")
    creditFormElement && creditFormElement.addEventListener('touchstart', blurActiveElementRef.current, false);

    return () => {
      creditFormElement && creditFormElement.removeEventListener('touchstart', blurActiveElementRef.current, false);
    }
  }, [])

  //Returns if element is text element or not
  function isTextInput(node) {
    return ['INPUT', 'TEXTAREA'].indexOf(node.nodeName) !== -1;
  }

  // Handle real-time validation errors from the card Element.
  const eventLogger = () => {
    if (!eventLogged) {
      setEventLogged(true);
      trackAnalyticsEvent('Interacted Plan Purchase Combined Page', { "Interaction Details": "Started Typing Credit Card Details" });
      trackAnalyticsEvent('Interacted All Plans Page ', { "Interaction Details": "Started Typing Credit Card Details" });

    }
  }
  const handleChange = (event) => {
    if(event.elementType === "cardNumber"){
      setCardNumberEnterInitiated(true);
    }
    if (event.error) {
      setError(event.error.message);
    } else if(cardNumberEnterInitiated && settings.campaignCode === "mastercard" && event.elementType === "cardNumber" && event.brand !== settings.campaignCode){
      setError("The card number entered is not a Mastercard. Please enter a valid card number as per the promotional offer.");
      setMastercardError(true);
    } else if(cardNumberEnterInitiated && settings.campaignCode === "mastercard" && event.elementType === "cardNumber" && event.brand === settings.campaignCode){
      setMastercardError(false);
      setError(null);
    } else {
      setError(null);
    }
    eventLogger();
  }

  // Handle form submission.
  const handleSubmit = async (event) => {
    event && event.preventDefault();
    mastercardError ? setError("The card number entered is not a Mastercard. Please enter a valid card number as per the promotional offer.") : setError(null);
    let promoValidate = await validatePromoCode();
    let emailValidated = validateEmail(email);
    errorElement && errorElement.current ? errorElement.current.scrollIntoView({ behavior: "smooth" }) : null;

    if (emailValidated && promoValidate && !mastercardError) {
      const card = elements.getElement(CardNumberElement);
      try {
        const result = await stripe.createToken(card);
        if (result.error) {
          trackAnalyticsEvent('Payment Form Error', { 'Error type': 'Wrong Card Details' });
          // Inform the user if there was an error.
          setError(result.error.message);
          // setSubmitCreditForm(false);
        } else {
          setError(null);
          // Send the token to your server.
          result['stripe_token'] = result.token.id
          saveAndSubscribe(result);
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const validateEmail = (email) => {
    let err = error;

    if (!email || email.trim().length == 0)
      err = "Email can't be blank";

    else if (email) {
      const emailRegex = /^[a-zA-Z0-9,!#\$%&'\*\+\/=\?\^_`\{\|}~-]+(\.[a-zA-Z0-9,!#\$%&'\*\+\/=\?\^_`\{\|}~-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{2,})$/;
      if (!emailRegex.test(email)) {
        err = "Email is invalid"
      }
    }

    setError(err);

    if (err)
      return false;

    return true;
  }

  const zipChangeHandler = (e) => {
    let zipCode = e.target.value;
    if (zipCode.length > 2) {
      Config.DataApi.billingZipCode(
        { postal_code: parseInt(zipCode) },
        (response) => {
          if (response && response.length) {
            response.forEach(el => {
              if (el.zip_code === (+zipCode)) {
                // this.setState({ postalCode });
                console.log('POSTAL CODE MATCHED : ', zipCode);
              }
            });
          }
        }
      );
    }
    eventLogger();
    setPostalCode(zipCode);
  }


  const saveAndSubscribe = (data) => {
    let params = { ...data } || {}
    params.email = email
    params['postal_code'] = showZipOnPurchase ? parseInt(postalCode) : '';
    paymentHandler(params);
  }

  const validatePromoCode = async e => {
    e && e.preventDefault();
    const campaign_code = localStorage.getItem("campaign_code");
    if (!["mastercard","miles"].includes(campaign_code)){
      return true;
    }

    if (!promo) {
      trackAnalyticsEvent('Error on Promo Code Page Shown', { "Error Type": "Invalid Promo Code" })
      setPromoCodeError("Please enter a valid offer code");
      return false;
    }
    const resp = await ValidateOfferCode({ code: promo, pc_code: campaign_code });

    if (resp && resp.valid == true) {
      setPromoValid(true);
      setPromoCodeError("");
      return true;
    }

    else {
      trackAnalyticsEvent('Error on Promo Code Page Shown', { "Error Type": "Invalid Promo Code" })
      setPromoValid(false);
      setPromoCodeError("Please enter a valid offer code");
      return false;
    }
  }
  const changePromo = (e) => {
    setPromoValid(false);
    setPromoCodeError("");
    setPromo(e.target.value)
  }
  let trialOrSubscription = settings.campaignCode === "miles"?'subscription':'trial';
  let TrialDateText = settings.trailDays ? `You will not be charged until your FREE ${trialOrSubscription} ends on ${getTrialEndDate(trailDays)}` : '';
  return (
    <React.Fragment>
    {
      settings.showPromoInputField === "true" && 
      <FormElement>
        <label>ENTER PROMO CODE</label>
        <InputBox text_color={ !promoValid && promoCodeError == "" ? "" : !promoValid && !!(promoCodeError) ? orange : mintGreen } >
          <input type="text" value={promo} onChange={(e) => changePromo(e)} required />
          <span onClick={validatePromoCode} className="link_style">{promoCodeError ? "Try Again" : (promoValid ? <S.ImageWrapper><Image src={tick} /> </S.ImageWrapper> : "Apply")}</span>
        </InputBox>
        {(!!(promoCodeError) || promoValid == true) && <S.MessageBox isError={!!(promoCodeError)}>
          <p>{promoCodeError ? promoCodeError : (promoValid ? promoValidMessage : "")}</p>
        </S.MessageBox>}
      </FormElement>
    }
    <form id="credit-card-form">
      { <>
        <StripeElementWrapper>
          <label>EMAIL ID</label>
          <input type="email" className="cc-num inp-brand"
            value={email}
            onChange={(e) => {
              handleChange(e);
              setEmail(e.target.value);
            }}
          />
        </StripeElementWrapper>
        <StripeElementWrapper>
          <label htmlFor="card-number-element">CARD NUMBER</label>
          <CardBrandIcons />
          <CardNumberElement
            id="card-number-element"
            options={CARD_ELEMENT_STYLES}
            onChange={handleChange}
            className="card-inp-element"
          />
        </StripeElementWrapper>
        <DateCvcElementWrap>
          <StripeElementWrapper marginRight={true}>
            <label htmlFor="card-expiry-element">EXPIRY DATE</label>
            <CardExpiryElement
              id="card-expiry-element"
              options={CARD_ELEMENT_STYLES}
              onChange={handleChange}
              className="card-inp-element"
            />
          </StripeElementWrapper>

          <StripeElementWrapper>
            <label htmlFor="card-cvc-element">CVC</label>
            <CardCvcElement
              id="card-cvc-element"
              options={CARD_ELEMENT_STYLES}
              onChange={handleChange}
              className="card-inp-element"
            />
          </StripeElementWrapper>
        </DateCvcElementWrap>

        {showZipOnPurchase ? <StripeElementWrapper>
          <label htmlFor="zipcode-element">PIN</label>
          <ZipCodeElement
            type="number"
            id="zipcode-element"
            className="card-inp-element"
            placeholder="Billing Zip Code"
            value={postalCode}
            maxLength={7}
            onChange={(e) => zipChangeHandler(e)}
          />
        </StripeElementWrapper> : null}
      </>}
      <CardError className="card-errors" role="alert" ref={errorElement}>{error}</CardError>

      <TrialDate>{TrialDateText}</TrialDate>
      <ButtonContainer>
        <PaymentCta clickHandler={handleSubmit} fromCreditForm={true} settings={settings} />
      </ButtonContainer>
      <SecureText zipCodeVisible={showZipOnPurchase}>Secure payment. We use 256-bit encryption. The same that banks use</SecureText>
      {showZipOnPurchase && <SecureText>*Taxes will be applicable as per the local tax laws.</SecureText>}
    </form>
    </React.Fragment>
  );
}

const CardBrandIcons = () => {
  return (
    <CardBrandsIconWrap>
      <Image src={Visa} />
      <Image src={Amex} />
      <Image src={Discover} />
      <Image src={MasterCard} />
    </CardBrandsIconWrap>
  )
}

const CreditCardForm = ({ settings, paymentHandler, error, setError, submitCreditForm, setSubmitCreditForm }) => {
  const stripePromise = loadStripe(settings.stripeKey);
  console.count('stripee');
  return (
    <Elements stripe={stripePromise} >
      <CheckoutForm
        settings={settings}
        paymentHandler={paymentHandler}
        error={error}
        setError={setError}
      />
    </Elements>
  );
}
export default CreditCardForm;

export const StripeElementWrapper = Styled(FormElement)`
  flex: 1;
  ${props => props.marginRight && `margin-right:3.3rem;`};
  .card-inp-element {
    padding: 1.6rem 0 0;
    border-bottom: 0.1rem solid #4F52C3;
  }
  label{
    ${fontQuicksand(1.2, 1.6, 7)}

  }
`;

export const DateCvcElementWrap = Styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ZipCodeElement = Styled.input`
  background: transparent;
  border: 0;
  border-bottom: 0.1rem solid #4F52C3;
  display: block;
  padding: 1rem 0 0;
  width: 100%;
  ${fontQuicksand(1.6, 2.0, 7)}
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  ::placeholder {
    color: #313575;
  }
`;

export const TrialDate = Styled.p`
  ${fontQuicksand(1.4, 1.8, 5)}
  margin: 1rem 0;
  @media ${device.PhoneLargeScreen} {
    ${fontQuicksand(1.1, 1.5, 5)}
  }
  @media ${device.Phone} {
    ${fontQuicksand(1.1, 1.5, 5)}
  }
  @media (min-width: ${breakpoints.lg}px) {
    margin: 0 0 1.2rem;
  }
`;

export const CardError = Styled.p`
  color: #EB6D45;
  ${fontQuicksand(1.2, 1.6, 5)};
  min-height: 1.7rem;
`;

export const SecureText = Styled.p`
  color: #9294F6;
  margin: 1rem 0 ${props => props.zipCodeVisible ? '1rem' : '3rem'};
  ${fontQuicksand(1.2, 1.6, 5)}
  @media ${device.PhoneLargeScreen} {
    ${fontQuicksand(1, 1.4, 5)}
  }
  @media ${device.Phone} {
    ${fontQuicksand(1, 1.4, 5)};
    margin-bottom: 1rem;
  }
  @media ${device.Tablet} and (orientation: portrait) {
    margin-bottom: 1rem;
  }
`;

export const CardBrandsIconWrap = Styled.div`
  position: absolute;
  right: 0;
  bottom: 1.4rem;
  img {
    margin-right: 0.5rem;
  }
`;

export const ButtonContainer = Styled.div`
  @media ${device.Phone}{
    width: 100%;
    height: 8.1rem;
    position: fixed;
    left: 0;
    bottom : 0rem;
    display: flex;
    justify-content: center;
    align-items: center;
    background: radial-gradient(79.59% 79.59% at 49.87% 20.41%, #141953 0%, rgba(20, 25, 83, 0.88) 0.01%, #141953 100%);
  }
`